import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  ShieldCheck, Buildings, Globe, Database,
  ChatText, CalendarCheck, ShieldWarning, UsersThree,
  Sliders, Translate,
  Camera, IdentificationCard, SpeakerHigh, WhatsappLogo,
  Globe as GlobeIcon, Books, TreeStructure,
} from "@phosphor-icons/react";

/* ─── Available Now ─── */
const liveFeatures = [
  {
    icon: ChatText,
    title: "Policy answers from your documents",
    desc: "Employees ask a question in chat. Mitti answers from the organisation's own uploaded policies and cites the source. If a policy is silent on the topic, Mitti says so and flags the question for HR to review.",
  },
  {
    icon: CalendarCheck,
    title: "Leave in chat",
    desc: "Apply, cancel and check leave status — all inside the conversation. Overlapping requests are blocked automatically. Approval requests go to the manager by email. HR sees every record.",
  },
  {
    icon: ShieldWarning,
    title: "Confidential grievances",
    desc: "Employees raise grievances with tracked response deadlines. Harassment and ethics cases reach only the designated POSH administrators and stay hidden from everyone else.",
  },
  {
    icon: UsersThree,
    title: "Employee master",
    desc: "HR adds or bulk-uploads employees and turns features on or off per employee from the console.",
  },
  {
    icon: Sliders,
    title: "Run by HR",
    desc: "Policies, employees, announcements, grievances and admin roles — managed from one console. Each administrator sees only what their role needs.",
  },
  {
    icon: Translate,
    title: "Multiple Indian languages",
    desc: "Employees can write in their own language, including romanised text. Mitti responds in the same language.",
  },
];

/* ─── In Development ─── */
const devFeatures = [
  { icon: Camera, title: "Bills", desc: "Employees will photograph a receipt, confirm the details Mitti reads, and send the claim to their manager for approval." },
  { icon: IdentificationCard, title: "Document tracking", desc: "Employees will submit visa and ID documents by photo. HR will review them from the console. Mitti will send expiry reminders to the employee and their manager." },
  { icon: SpeakerHigh, title: "Voice", desc: "Speak a question and hear the answer, in Indian languages. Voice input and output are being built to work across all existing features." },
  { icon: WhatsappLogo, title: "WhatsApp", desc: "The same assistant, inside WhatsApp. No separate app, no new login." },
  { icon: GlobeIcon, title: "Arabic", desc: "Support for Arabic-speaking teams is being built to serve Gulf deployments." },
  { icon: Books, title: "Organisation knowledge base", desc: "A dedicated knowledge base per organisation, so each client's assistant answers only from that client's documents." },
  { icon: TreeStructure, title: "Configurable workflows", desc: "HR will set leave types, grievance categories and approval chains from the console — no code, no IT ticket." },
];

/* ─── Architecture ─── */
const archItems = [
  { icon: Buildings, title: "Per-client isolation", desc: "Each client gets a dedicated tenant. Documents, conversations, and audit logs physically separated. No shared models. No cross-contamination." },
  { icon: ShieldCheck, title: "VAPT-ready architecture", desc: "No API keys on the frontend. No exposed endpoints. Built for enterprise security audits from day one — not retrofitted." },
  { icon: Globe, title: "Data residency by region", desc: "Deployments hosted in the jurisdiction your data requires. Your data stays where it should." },
  { icon: Database, title: "Full audit trail", desc: "Every question, every answer, every action logged immutably. Exportable for compliance reviews, internal audits, or regulatory submissions." },
];

/* ─── Thesis lines ─── */
const thesisLines = [
  "HR doesn't need another tool. It needs a system that absorbs the work, not adds to it.",
  "Mitti starts where the work starts — at the employee question.",
  "Your name, your policy, data in your control.",
  "Each capability earns its place before the next is built.",
  "You're not buying a roadmap. You're buying what's live today — and inheriting what comes next.",
];

/* ─── Line Reveal Animation ─── */
function RevealLine({ children, delay = 0, className = "", style = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div
        initial={{ y: "110%" }}
        animate={inView ? { y: 0 } : { y: "110%" }}
        transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
        className={className}
        style={style}
      >
        {children}
      </motion.div>
    </div>
  );
}

/* ─── Feature Card ─── */
function FeatureCard({ feature, index, isDev = false }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });

  return (
    <motion.div
      ref={ref}
      data-testid={`mitti-feature-${isDev ? "dev" : "live"}-${index}`}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative p-6 sm:p-7"
      style={{
        border: "1px solid var(--sv-border)",
        background: hovered ? "var(--sv-bg-card-hover)" : "transparent",
        borderColor: hovered ? "var(--sv-border-hover)" : "var(--sv-border)",
        transition: "background 0.35s ease, border-color 0.35s ease",
      }}
    >
      {isDev && (
        <span
          className="absolute top-4 right-4 text-[9px] tracking-[0.2em] uppercase px-2.5 py-1"
          style={{
            fontFamily: "var(--sv-mono-font)",
            color: "var(--sv-text-muted)",
            border: "1px solid var(--sv-border)",
            opacity: 0.7,
          }}
        >
          In development
        </span>
      )}
      <feature.icon
        size={22}
        weight="regular"
        className="mb-5"
        style={{
          color: hovered ? "var(--sv-gold)" : "var(--sv-text-muted)",
          transition: "color 0.3s ease",
        }}
      />
      <h3
        className="text-base font-medium mb-3"
        style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
      >
        {feature.title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: "var(--sv-text-muted)" }}>
        {feature.desc}
      </p>
    </motion.div>
  );
}

/* ─── Architecture Card ─── */
function ArchCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="flex gap-4"
    >
      <item.icon size={22} weight="regular" className="flex-shrink-0 mt-0.5" style={{ color: "var(--sv-gold)" }} />
      <div>
        <h4 className="text-base font-medium mb-2" style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}>
          {item.title}
        </h4>
        <p className="text-sm leading-relaxed" style={{ color: "var(--sv-text-muted)" }}>{item.desc}</p>
      </div>
    </motion.div>
  );
}

export default function Mitti() {
  return (
    <section id="mitti" data-testid="mitti-section" className="relative">

      {/* ─── Hero ─── */}
      <div className="pt-32 sm:pt-40 pb-24 sm:pb-32 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <RevealLine delay={0}>
          <p className="overline mb-6">Mitti</p>
        </RevealLine>

        <RevealLine delay={0.1}>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]"
            style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
          >
            HR operations,
          </h1>
        </RevealLine>
        <RevealLine delay={0.2}>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]"
            style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
          >
            run by <span className="metallic-text">HR.</span>
          </h1>
        </RevealLine>

        <RevealLine delay={0.4}>
          <p
            className="mt-8 text-base sm:text-lg leading-relaxed max-w-2xl"
            style={{ color: "var(--sv-text-body)" }}
          >
            One assistant for employees. One console for HR.
            Leaves, grievances and policy answers today; bills and documents next.
          </p>
        </RevealLine>
      </div>

      {/* ─── Divider ─── */}
      <div className="section-divider" />

      {/* ─── Available Now ─── */}
      <div className="py-24 sm:py-32 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <RevealLine>
          <p className="overline mb-5">Available now</p>
        </RevealLine>
        <RevealLine delay={0.1}>
          <p
            className="text-sm max-w-lg mb-14 leading-relaxed"
            style={{ color: "var(--sv-text-muted)" }}
          >
            Live and tested. Deployed in days, not months.
          </p>
        </RevealLine>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--sv-border)" }}>
          {liveFeatures.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} />
          ))}
        </div>
      </div>

      {/* ─── Divider ─── */}
      <div className="section-divider" />

      {/* ─── In Development ─── */}
      <div className="py-24 sm:py-32 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <RevealLine>
          <p className="overline mb-5">In development</p>
        </RevealLine>
        <RevealLine delay={0.1}>
          <p
            className="text-sm max-w-lg mb-14 leading-relaxed"
            style={{ color: "var(--sv-text-muted)" }}
          >
            Each capability is being built to the same standard as what's live. Nothing ships until it's ready.
          </p>
        </RevealLine>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--sv-border)" }}>
          {devFeatures.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} isDev />
          ))}
        </div>
      </div>

      {/* ─── Divider ─── */}
      <div className="section-divider" />

      {/* ─── Architecture ─── */}
      <div className="py-24 sm:py-32 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <RevealLine>
          <p className="overline mb-5">Architecture</p>
        </RevealLine>
        <RevealLine delay={0.1}>
          <h3
            className="text-2xl sm:text-3xl font-semibold tracking-tight max-w-3xl mb-14"
            style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
          >
            Enterprise-grade. From day one.
          </h3>
        </RevealLine>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-12">
          {archItems.map((item, i) => (
            <ArchCard key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>

      {/* ─── Divider ─── */}
      <div className="section-divider" />

      {/* ─── Thesis ─── */}
      <div className="py-24 sm:py-32 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <RevealLine>
          <p className="overline mb-8">The thesis</p>
        </RevealLine>

        <div className="max-w-3xl space-y-5">
          {thesisLines.map((line, i) => (
            <RevealLine key={i} delay={i * 0.08}>
              <p
                className="text-base sm:text-lg leading-relaxed"
                style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-secondary)" }}
              >
                {line}
              </p>
            </RevealLine>
          ))}
        </div>
      </div>

      {/* ─── Divider ─── */}
      <div className="section-divider" />

      {/* ─── CTA ─── */}
      <div className="py-24 sm:py-32 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <a
            data-testid="mitti-cta-btn"
            href="#contact"
            className="btn-shine font-bold tracking-wide px-8 py-4 text-sm transition-colors inline-block"
            style={{ backgroundColor: "var(--sv-gold)", color: "var(--sv-bg-primary)" }}
          >
            Request a Mitti Demo
          </a>
          <p
            className="text-sm"
            style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-muted)", fontStyle: "italic" }}
          >
            Deployed in days. No integration required.
          </p>
        </div>

        <p
          className="mt-16 text-xs tracking-[0.15em] uppercase"
          style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-text-muted)", opacity: 0.5 }}
        >
          The Siveli Desk
        </p>
      </div>
    </section>
  );
}
